// src/gtm.ts
import { VITE_GTM_ID, VITE_APP_ENV } from '@/env'

// Read env at call-time (env values are populated asynchronously via initEnv)
function getGtmId(): string | undefined {
	return VITE_GTM_ID()
}
function getAppEnv(): string {
	return VITE_APP_ENV() ?? 'development'
}

/**
 * Options:
 *  - onlyInProd: if true (default) will only init when import.meta.env.MODE === 'production'
 *  - dataLayerName: allow custom dataLayer name (default 'dataLayer')
 *  - waitForConsent: if true, will *not* inject script — you should call initGtm({waitForConsent: true}) once consent is granted
 */
export type InitOptions = {
	onlyInProd?: boolean
	dataLayerName?: string
	waitForConsent?: boolean
}

declare global {
	interface Window {
		// default name; if you use a custom name you can still cast as any
		dataLayer?: Array<Record<string, unknown>>
		[key: string]: unknown
	}
}

function isBrowser() {
	return typeof window !== 'undefined' && typeof document !== 'undefined'
}

export function isGtmEnabled(opts?: { onlyInProd?: boolean }) {
	if (!getGtmId()) return false
	const onlyInProd = opts?.onlyInProd ?? true
	if (!onlyInProd) return true
	return getAppEnv() === 'production'
}

/**
 * Initialize GTM (idempotent). Returns true if script exists or was inserted.
 */
export function initGtm(options: InitOptions = {}): boolean {
	if (!isBrowser()) return false
	const gtmId = getGtmId()
	if (!gtmId) return false
	const onlyInProd = options.onlyInProd ?? true
	if (onlyInProd && getAppEnv() !== 'production') return false

	const dataLayerName = options.dataLayerName || 'dataLayer'

	// If caller asked to wait for consent, do not inject.
	if (options.waitForConsent) return false

	// don't inject twice
	if (document.getElementById('gtm-script')) return true

	// Ensure dataLayer exists and push gtm.start (declaration BEFORE loading gtm.js is important)
	const win = window as Window & Record<string, unknown>
	win[dataLayerName] = (win[dataLayerName] as Array<Record<string, unknown>> | undefined) || []
	;(win[dataLayerName] as Array<Record<string, unknown>>).push({
		'gtm.start': Date.now(),
		event: 'gtm.js',
	})

	// Create script element that loads gtm.js directly (avoids inline IIFE which breaks strict CSP)
	const script = document.createElement('script')
	script.id = 'gtm-script'
	script.async = true
	// If you use a custom dataLayer variable name, include &l=... per GTM docs
	const dlParam = dataLayerName === 'dataLayer' ? '' : `&l=${encodeURIComponent(dataLayerName)}`
	script.src = `https://www.googletagmanager.com/gtm.js?id=${encodeURIComponent(gtmId)}${dlParam}`
	document.head.appendChild(script)

	// Insert noscript iframe (idempotent). Place as first child of body if possible.
	const noscriptId = 'gtm-noscript'
	if (!document.getElementById(noscriptId)) {
		const nos = document.createElement('noscript')
		nos.id = noscriptId
		nos.innerHTML = `<iframe src="https://www.googletagmanager.com/ns.html?id=${encodeURIComponent(
			gtmId
		)}" height="0" width="0" style="display:none;visibility:hidden"></iframe>`
		if (document.body && document.body.firstChild) {
			document.body.insertBefore(nos, document.body.firstChild)
		} else if (document.body) {
			document.body.appendChild(nos)
		}
	}

	return true
}

/**
 * Push an event/object to the dataLayer (works even before GTM is loaded)
 */
export function pushGtmEvent(event: Record<string, unknown>, dataLayerName = 'dataLayer') {
	if (!isBrowser()) return
	const win = window as Window & Record<string, unknown>
	win[dataLayerName] = (win[dataLayerName] as Array<Record<string, unknown>> | undefined) || []
	;(win[dataLayerName] as Array<Record<string, unknown>>).push(event)
}
